/**
 * Brindes automáticos
 *
 * Reconcilia as linhas de brinde com o que o snippet `gift-config` decidiu no
 * servidor. Toda a condição (valor, tag, coleção, produto) é avaliada em Liquid
 * a partir de Configurações do tema → NX - Brinde *; aqui só sobra: adicionar,
 * remover, colapsar duplicatas e trocar a variante.
 *
 * São quatro brindes independentes e mais de um pode acontecer ao mesmo tempo,
 * mas a reconciliação é por PRODUTO, não por regra: duas regras apontando para
 * o mesmo produto brinde rendem uma linha só. A propriedade _nx_brinde guarda
 * qual regra colocou a linha, e é ela que escolhe o selo exibido.
 *
 * Fluxo de uma mudança no carrinho:
 *   1. algum componente do tema publica PUB_SUB_EVENTS.cartUpdate
 *   2. buscamos ?section_id=gift-promotion (uma seção-casca que só renderiza o
 *      snippet) e trocamos o JSON de config
 *   3. se a config discorda do carrinho, mutamos o carrinho pedindo de volta
 *      as mesmas seções que o tema renderiza (drawer/itens/bubble) + a nossa
 *   4. aplicamos essas seções e repetimos até convergir
 *
 * O cliente não consegue remover um brinde: o drawer e a página /cart não
 * renderizam quantidade nem botão de remover nessas linhas. Se ele remover por
 * outro caminho e ainda estiver elegível, o passo 3 devolve o brinde.
 */
(function () {
  'use strict';

  var SECTION_ID = 'gift-promotion';
  var CONFIG_ID = 'gift-config';
  var SOURCE = 'gift-promotion';
  var MAX_STEPS = 4;

  var running = false;
  var queued = false;
  var scheduleTimer = null;

  function addUrl() {
    return (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
  }

  function changeUrl() {
    return (window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js';
  }

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      var cfg = JSON.parse(el.textContent);
      if (!cfg) return null;
      cfg.rules = Array.isArray(cfg.rules) ? cfg.rules : [];
      cfg.giftLines = Array.isArray(cfg.giftLines) ? cfg.giftLines : [];
      return cfg;
    } catch (e) {
      return null;
    }
  }

  /* --------------------------- render de seções --------------------------- */

  // `cart-drawer-items` / `cart-items` já sabem quais seções o tema precisa
  // trocar e como recortar cada uma. Reaproveitamos em vez de duplicar a lista.
  function cartHost() {
    return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  }

  function themeSections() {
    var host = cartHost();
    if (!host || typeof host.getSectionsToRender !== 'function') return [];
    try {
      return host.getSectionsToRender() || [];
    } catch (e) {
      return [];
    }
  }

  function sectionsToRequest() {
    var ids = [SECTION_ID];
    themeSections().forEach(function (section) {
      if (section.section && ids.indexOf(section.section) === -1) ids.push(section.section);
    });
    if (ids.indexOf('cart-icon-bubble') === -1) ids.push('cart-icon-bubble');
    return ids;
  }

  function applyConfigHTML(html) {
    var current = document.getElementById(CONFIG_ID);
    if (!current || !html) return false;
    var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(CONFIG_ID);
    if (!fresh) return false;
    current.textContent = fresh.textContent;
    return true;
  }

  function applySections(sections) {
    if (!sections) return;

    // A nossa config primeiro: o próximo passo do laço depende dela.
    if (sections[SECTION_ID]) applyConfigHTML(sections[SECTION_ID]);

    var host = cartHost();
    if (!host) return;

    themeSections().forEach(function (section) {
      var container = document.getElementById(section.id);
      var html = sections[section.section];
      if (!container || !html) return;
      var target = (section.selector && container.querySelector(section.selector)) || container;
      try {
        target.innerHTML = host.getSectionInnerHTML(html, section.selector);
      } catch (e) {
        /* seção fora desta página */
      }
    });
  }

  function toggleEmptyState(data) {
    if (!data || typeof data.item_count !== 'number') return;
    var isEmpty = data.item_count === 0;
    var host = cartHost();
    var drawer = document.querySelector('cart-drawer');
    var footer = document.getElementById('main-cart-footer');
    var order = document.querySelector('.cart-order');
    if (host) host.classList.toggle('is-empty', isEmpty);
    if (drawer) drawer.classList.toggle('is-empty', isEmpty);
    if (footer) footer.classList.toggle('is-empty', isEmpty);
    if (order) order.classList.toggle('is-empty', isEmpty);
  }

  function announce(data) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: data });
    }
  }

  /**
   * `?section_id=` (singular) renderiza a seção pelo NOME DO ARQUIVO, mesmo que
   * ela não esteja em template nenhum — é o mesmo parâmetro que o cart.js do
   * tema usa para buscar o cart-drawer, que também não está em template.
   *
   * O plural `?sections=` resolve o id DENTRO do template da página atual, então
   * numa página de produto ele devolvia `{"product": ...}` e a config nunca era
   * atualizada: o motor seguia lendo o estado velho e o brinde só entrava depois
   * de um reload. No corpo do POST das rotas /cart/*.js o plural funciona, e é
   * por isso que só esta busca estava quebrada.
   */
  function refreshConfig() {
    return fetch(window.location.pathname + '?section_id=' + SECTION_ID)
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (html) {
        if (html && !applyConfigHTML(html)) {
          console.error('[Brinde] a seção ' + SECTION_ID + ' respondeu sem a config: brinde não vai reagir');
        }
      })
      .catch(function () {
        /* mantém a config anterior */
      });
  }

  /* ------------------------------ mutações ------------------------------ */

  function mutate(url, payload) {
    payload.sections = sectionsToRequest();
    payload.sections_url = window.location.pathname;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) return null;
        applySections(result.data.sections);
        toggleEmptyState(result.data);
        return result.data;
      });
  }

  function firstAvailableVariantId(rule) {
    var variants = Array.isArray(rule.variants) ? rule.variants : [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i].id;
    }
    return null;
  }

  function changeLine(lineKey, quantity) {
    return mutate(changeUrl(), { id: lineKey, quantity: quantity });
  }

  // /cart/change.js mexe numa linha por vez. Encadeamos para resolver todas as
  // pendências de remoção/quantidade num passo só do laço.
  function applyLineChanges(changes) {
    return function () {
      return changes.reduce(function (chain, change) {
        return chain.then(function () {
          return changeLine(change.key, change.quantity);
        });
      }, Promise.resolve());
    };
  }

  /* --------------------- brinde que veio cobrado --------------------- */

  /**
   * O brinde pode ser um produto ativo da loja, zerado por um desconto
   * automático da Shopify. Se esse desconto não aplicar, o cliente seria
   * cobrado por um "brinde" — então a linha sai do carrinho.
   *
   * O produto fica marcado pelo resto da sessão: sem isso, remover e voltar a
   * adicionar (a condição continua satisfeita) viraria um laço infinito.
   *
   * Só marca quando ALGUMA regra ainda quer aquele produto. Uma linha cobrada
   * sem regra elegível é o caso comum, não falha de desconto: o carrinho caiu
   * abaixo do valor mínimo e o desconto da Shopify parou de valer junto. Essa
   * linha só sai. Antes ela também era marcada, e o cliente que voltava a
   * passar do valor mínimo na mesma aba nunca mais recebia o brinde.
   *
   * A chave ganhou _v2 para descartar as marcações erradas feitas pela regra
   * antiga nas abas que já estavam abertas.
   */
  var CHARGED_KEY = 'nx_gift_charged_v2';

  function chargedProducts() {
    try {
      return JSON.parse(sessionStorage.getItem(CHARGED_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function blockChargedProduct(productId) {
    try {
      var blocked = chargedProducts();
      blocked[productId] = true;
      sessionStorage.setItem(CHARGED_KEY, JSON.stringify(blocked));
    } catch (e) {
      /* sem storage: a linha ainda sai, só volta a ser tentada na próxima página */
    }
  }

  /**
   * Regras que devem render brinde agora, no máximo uma por produto: a primeira
   * regra que quer um produto é a dona da linha — e do selo.
   */
  function desiredRules(cfg) {
    var out = [];
    var seen = {};
    var blocked = chargedProducts();
    cfg.rules.forEach(function (rule) {
      if (!rule.enabled || !rule.eligible || !rule.available || !rule.productId) return;
      if (seen[rule.productId] || blocked[rule.productId]) return;
      if (!firstAvailableVariantId(rule)) return;
      seen[rule.productId] = true;
      out.push(rule);
    });
    return out;
  }

  function planAction(cfg) {
    // Bloqueia antes de decidir: o produto marcado sai de `desiredRules`, e a
    // linha dele cai no passo 1 como brinde que ninguém quer mais.
    cfg.giftLines.forEach(function (line) {
      if (!line.charged) return;
      var stillEligible = cfg.rules.some(function (rule) {
        return rule.enabled && rule.eligible && rule.productId === line.productId;
      });
      if (stillEligible) blockChargedProduct(line.productId);
    });

    var desired = desiredRules(cfg);
    var wanted = {};
    desired.forEach(function (rule) {
      wanted[rule.productId] = rule;
    });

    // 1. tira linha de brinde que ninguém quer mais, tira duplicata do mesmo
    //    produto e conserta quantidade diferente de 1.
    var changes = [];
    var keptByProduct = {};

    cfg.giftLines.forEach(function (line) {
      if (!wanted[line.productId] || keptByProduct[line.productId]) {
        changes.push({ key: line.key, quantity: 0 });
        return;
      }
      keptByProduct[line.productId] = line;
      if (line.quantity !== 1) {
        changes.push({ key: line.key, quantity: 1 });
      }
    });

    if (changes.length) return applyLineChanges(changes);

    // 2. adiciona os que faltam, todos no mesmo request
    var items = [];
    desired.forEach(function (rule) {
      if (keptByProduct[rule.productId]) return;
      items.push({
        id: firstAvailableVariantId(rule),
        quantity: 1,
        properties: { _nx_brinde: rule.key },
      });
    });

    if (items.length) {
      return function () {
        return mutate(addUrl(), { items: items });
      };
    }

    return null;
  }

  function step(depth) {
    if (depth >= MAX_STEPS) return Promise.resolve();

    var cfg = readConfig();
    if (!cfg) return Promise.resolve();

    var action = planAction(cfg);
    if (!action) return Promise.resolve();

    return action().then(function (data) {
      if (data) announce(data);
      return step(depth + 1);
    });
  }

  function run() {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    step(0)
      .catch(function (e) {
        console.error('[Brinde]', e);
      })
      .then(function () {
        running = false;
        if (queued) {
          queued = false;
          run();
        }
      });
  }

  function schedule() {
    clearTimeout(scheduleTimer);
    scheduleTimer = setTimeout(function () {
      refreshConfig().then(run);
    }, 250);
  }

  /* ------------------------- troca de variante ------------------------- */

  // /cart/change.js não troca variante: remove a linha e adiciona a nova,
  // preservando a regra dona para o selo continuar o mesmo.
  function swapVariant(lineKey, variantId, ruleKey) {
    if (running) return;
    running = true;

    document.querySelectorAll('.gift-picker').forEach(function (picker) {
      picker.classList.add('is-busy');
    });

    changeLine(lineKey, 0)
      .then(function (removed) {
        // Se a remoção falhou, adicionar aqui criaria uma segunda linha.
        if (!removed) return null;
        return mutate(addUrl(), {
          items: [{ id: variantId, quantity: 1, properties: { _nx_brinde: ruleKey || 'valor' } }],
        });
      })
      .then(function (data) {
        if (data) announce(data);
      })
      .catch(function (e) {
        console.error('[Brinde] troca de variante', e);
        document.querySelectorAll('.gift-picker').forEach(function (picker) {
          picker.classList.remove('is-busy');
        });
      })
      .then(function () {
        running = false;
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-gift-variant]');
    if (!button || button.disabled) return;

    event.preventDefault();
    var variantId = parseInt(button.getAttribute('data-gift-variant'), 10);
    var lineKey = button.getAttribute('data-gift-key');
    if (!variantId || !lineKey || button.classList.contains('is-selected')) return;

    swapVariant(lineKey, variantId, button.getAttribute('data-gift-rule'));
  });

  /* ------------------------------- start ------------------------------- */

  function bind() {
    if (window.__giftPromotionBound) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    window.__giftPromotionBound = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === SOURCE) return;
      schedule();
    });
  }

  function start() {
    bind();
    // A config veio renderizada com o carrinho desta página: já está fresca.
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
